import { animate, style, transition, trigger } from '@angular/animations';
import { injectUrlViewState } from '../../../utils/url-view-state.util';
import {
  Component,
  OnInit,
  OnDestroy,
  HostListener,
  inject,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { CommonModule } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { DashboardService } from '../../../services/dashboard.service';
import { ChatService, ChatStreamEvent } from '../../../services/chat.service';
import { CommonChatService } from '../../../services/common-chat.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { Router, RouterLink } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { RestaurantService } from '../../../services/restaurant.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { BaseChartDirective } from 'ng2-charts';
import { ChartData, ChartOptions } from 'chart.js';
import { FNFResponse } from '../../../models/response';
import { ChatPanelMessage } from '../../../models/chat-panel-message';
import {
  ChatMetricToken,
  extractTokensFromPayload,
  normalizeApiTokens,
} from '../../../utils/chat-message-format.util';
import { buildMessagesHtmlCache } from '../../../utils/chat-message-cache.util';
import { isPosNotSelected } from '../../../utils/pos-provider.util';
import { FEATURE_FLAG_KEYS, FeatureFlag, isFeatureActive } from '../../../utils/feature-flag.util';
import {
  formatUtcChatListUpdatedAt,
  formatUtcDate,
  formatUtcDateTime,
  utcNowIso,
} from '../../../utils/utc-date.util';
import { ChatPanelMessagesComponent } from './chat-panel-messages.component';
import { SubscriptionService } from '../../../services/subscription.service';

export type { ChatPanelMessage } from '../../../models/chat-panel-message';

export interface RestaurantChatSummary {
  restaurantId: string;
  chatId: string;
  chatType: string;
  insightId: string | null;
  createdAt: string;
  updatedAt: string;
  /** Custom display name from API (optional). */
  title?: string;
}

export interface StatMiniCard {
  category: string;
  salesText: string;
  salesTone: 'default' | 'success' | 'danger';
  itemName: string;
  imageUrl: string;
  borderColor: string;
}

export interface InsightRow {
  /** Figma: icon chip tone + icon glyph */
  // iconTone: 'pink' | 'purple' | 'amber' | 'mint' | 'rose' | 'blue';
  // icon: string;
  // title: string;
  // description: string;
  insightID: string;
  insight: string;
  insightValue: string;
  status: string;
  generationStatus: string;
  name: string;
}

export interface OfferItem {
  title: string;
  description: string;
  price: string;
  wasPrice: string;
  discount: string;
  imageUrl: string;
}

export interface CompactStatCard {
  title: string;
  value: string;
  /** Boxicons class, used when svgIcon is not set. */
  icon: string;
  /** Name of an SVG registered with MatIconRegistry; takes precedence. */
  svgIcon?: string;
  tone: 'pink' | 'purple' | 'amber' | 'mint' | 'rose' | 'blue' | 'dark';
}

@Component({
  selector: 'app-dashboard2',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    MatTooltipModule,
    MatIconModule,
    BaseChartDirective,
    ChatPanelMessagesComponent,
  ],
  templateUrl: './dashboard2.component.html',
  styleUrl: './dashboard2.component.css',
  animations: [
    trigger('insightSlide', [
      transition(':increment', [
        style({ opacity: 0, transform: 'translateX(16px)' }),
        animate(
          '320ms cubic-bezier(0.22, 1, 0.36, 1)',
          style({ opacity: 1, transform: 'translateX(0)' }),
        ),
      ]),
      transition(':decrement', [
        style({ opacity: 0, transform: 'translateX(-16px)' }),
        animate(
          '320ms cubic-bezier(0.22, 1, 0.36, 1)',
          style({ opacity: 1, transform: 'translateX(0)' }),
        ),
      ]),
      transition('* => *', [
        style({ opacity: 0 }),
        animate('280ms ease-out', style({ opacity: 1 })),
      ]),
    ]),
  ],
})
export class Dashboard2Component implements OnInit, OnDestroy {
  private readonly doc = inject(DOCUMENT);

  @ViewChild(ChatPanelMessagesComponent)
  private chatPanelMessages?: ChatPanelMessagesComponent;

  private readonly sanitizer = inject(DomSanitizer);

  userName = 'Jane';
  restaurantName = 'King Chola';
  chatPlaceholder = 'What do you think ...';
  chatInput = '';

  /** Figma side chat panel */
  chatPanelOpen = false;
  readonly chatPanelMinWidth = 320;
  readonly chatPanelMaxWidth = 720;
  readonly chatPanelDefaultWidth = 420;
  private static readonly CHAT_PANEL_WIDTH_STORAGE_KEY = 'd2_chat_panel_width';
  chatPanelWidth = this.chatPanelDefaultWidth;
  chatPanelResizing = false;
  private chatPanelResizeStartX = 0;
  private chatPanelResizeStartWidth = 0;
  /** Short line under the panel title (insight context or typed prompt) */
  chatPanelSubtitle: string | null = null;
  chatPanelName: string | null = null;
  chatPanelInsightId: string | null = null;
  chatPanelInsightValue: string | null = null;
  chatPanelChatId: string = '';
  panelMessages: ChatPanelMessage[] = [];
  panelComposer = '';

  /** True when the message panel was opened from chat history (shows Back to History). */
  chatOpenedFromHistory = false;

  /** Chat history side panel */
  chatListPanelOpen = false;
  allChats: RestaurantChatSummary[] = [];
  chatsLoading = false;
  chatsLoadingMore = false;
  chatsCursor = '';
  readonly chatsListLimit = 20;
  deletingChatId: string | null = null;
  renamingChatId: string | null = null;
  /** Chat id whose row action menu is open. */
  openChatListMenuId: string | null = null;
  /** Inline title edit (ChatGPT-style). */
  editingChatTitleId: string | null = null;
  editingChatTitleDraft = '';
  private editingChatTitleOriginal = '';
  private chatTitleBlurCommitDisabled = false;

  @ViewChild('chatTitleInput') private chatTitleInput?: ElementRef<HTMLInputElement>;

  /** Shared soup thumb matches Figma; burger for most ordered */
  private soupThumb =
    'https://images.unsplash.com/photo-1547592166-23ac45744acd?w=96&h=96&fit=crop';

  statCards: StatMiniCard[] = [
    {
      category: 'Highest Priced Item',
      salesText: '0 Sales',
      salesTone: 'default',
      itemName: 'Coming Soon ...',
      imageUrl: './../../../../assets/default/menu-thump.png',
      borderColor: '2px solid #F1ECFE',
    },
    {
      category: 'Most Ordered Item',
      salesText: '0 Sales',
      salesTone: 'default',
      itemName: 'Coming Soon ...',
      imageUrl: './../../../../assets/default/menu-thump.png',
      borderColor: '2px solid #F0F9F3',
    },
    {
      category: 'Least Ordered Item',
      salesText: '0 Sales',
      salesTone: 'default',
      itemName: 'Coming Soon ...',
      imageUrl: './../../../../assets/default/menu-thump.png',
      borderColor: '2px solid #FEF4EB',
    },
    {
      category: 'Customer Favorite Item',
      salesText: '0 Sales',
      salesTone: 'default',
      itemName: 'Coming Soon ...',
      imageUrl: './../../../../assets/default/menu-thump.png',
      borderColor: '2px solid #FDEDF0',
    },
    {
      category: 'Least Favorite Item',
      salesText: '0 Sales',
      salesTone: 'default',
      itemName: 'Coming Soon ...',
      imageUrl: './../../../../assets/default/menu-thump.png',
      borderColor: '2px solid #EBF4FF',
    },
  ];

  insights1 = [
    {
      iconTone: 'pink',
      icon: 'bx bx-line-chart',
      title: "45% of customers order 'French Fries' alongside the 'Classic Burger'.",
      description:
        'Create a Bundle Deal to increase Average Order Value by an estimated $3.50 per check.',
    },
    {
      iconTone: 'purple',
      icon: 'bx bx-user',
      title: 'Waiter Sarah generated 42% of all dessert sales this week.',
      description: `Have her share her upsell pitch with the team or run a "Dessert Duel" staff contest.`,
    },
    {
      iconTone: 'amber',
      icon: 'bx bx-time-five',
      title: 'Your Tuesday lunch traffic is 35% lower than your weekday average.',
      description: 'Launch a lunch special between 11 AM-2 PM to attract nearby office workers.',
    },
    {
      iconTone: 'mint',
      icon: 'bx bx-star',
      title: 'Promote your top-selling items to drive more orders.',
      description: 'Badge your top 3 dishes as "Most Popular" - they drive 68% of your revenue.',
    },
    {
      iconTone: 'rose',
      icon: 'bx bx-trash',
      title: "Remove or replace menu items that haven't sold in 30 days.",
      description:
        'A shorter menu reduces confusion and helps customers decide faster, boosting orders.',
    },
    {
      iconTone: 'blue',
      icon: 'bx bx-message-rounded',
      title: 'Respond to every customer review within 24 hours.',
      description: 'Quick replies build trust and increase repeat visits by up to 33%.',
    },
  ];

  insights: InsightRow[] = [];
  allInsightsHaveValue = true;
  insightsLoading = true;

  //   insights: InsightRow[] = [
  //     {
  //         "name": "Combo suggestion",
  //         "insightID": "insight-combo-suggestion-v1",
  //         "insight": "0.1% of orders include 'Frank's Favorite Drink', 'Pannaer pakoda', and 'Shan's Dish 3', and when one is ordered, there's a 100.0% likelihood the others are too.",
  // "insightValue": "With a perfect trend interest score of 100, this combo is catching eyes online, making it a timely choice for next week. Despite the warm weather, the buzz around 'Frank's Favorite Drink', 'Pannaer pakoda', and 'Shan's Dish 3' suggests they could attract customers eager for what's trending. Highlighting this trio could lift average check size as diners opt for this popular combination.",
  // "status": "active",
  // "generationStatus": "completed"
  //     },
  //     {
  //         "name": "Non-popular menu items",
  //         "insightID": "insight-non-popular-v1",
  //         "insight": "'12 Layer Grilled Cheese' has 0 orders, making it 0.0% of your menu average.",
  // "insightValue": "Consider simplifying the '12 Layer Grilled Cheese' to make it more appealing. This week, focus on highlighting its unique layers on the menu and offer a tasting to entice customers. This strategy could help lift average check size.",
  // "status": "active",
  // "generationStatus": "completed"
  //     },
  //     {
  //         "name": "Popular menu items",
  //         "insightID": "insight-popular-v1",
  //         "insight": "'Unknown Item' generated $900, accounting for 25.6% of total revenue, highlighting its significant impact on sales.",
  // "insightValue": "The success of 'Unknown Item' is likely driven by its premium appeal or pricing, making it a key revenue contributor. To sustain and grow this revenue, consider enhancing its visibility on the menu or bundling it with complementary items. To lift average check size, leverage the popularity of 'Unknown Item' by pairing it with a mid-tier item, such as a side or drink, encouraging customers to try more of your offerings.",
  // "status": "active",
  // "generationStatus": "completed"
  //     }
  // ];

  readonly summaryTabs: string[] = ['Orders', 'Tables', 'Dishes', 'Bill'];
  activeSummaryTab = 'Orders';
  /** The chart metric each Summary tab reads. */
  private readonly summaryMetrics: Record<string, string> = {
    Orders: 'orders',
    Tables: 'tables',
    Dishes: 'menus',
    Bill: 'bill',
  };
  /** Stat Overview period, Summary period and Summary tab live in the URL
   * (?stats, ?summary, ?tab), so a browser reload reopens the same view. */
  private urlState = injectUrlViewState();

  barChartData: ChartData<'bar'> = {
    labels: [],
    datasets: [
      {
        data: [],
        backgroundColor: [],
        // Placeholder only: applyChartTheme() swaps in the live --c-brand
        // before the chart is drawn, and again whenever the theme changes.
        hoverBackgroundColor: '#b31d2c',
        borderRadius: 4,
        borderSkipped: false,
        label: '',
      },
    ],
  };

  barChartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        displayColors: false,
        callbacks: {
          label: (ctx) => ` ${ctx.parsed.y}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { color: '#6c7689', font: { size: 9, family: 'Inter' } },
      },
      y: {
        grid: { color: 'rgba(232, 234, 237, 0.7)' },
        border: { display: false },
        ticks: { color: '#6c7689', font: { size: 10, family: 'Inter' }, maxTicksLimit: 6 },
        beginAtZero: true,
      },
    },
  };

  statPeriods: any = [
    { title: 'Last Week', value: '7D' },
    { title: 'Last 2 Weeks', value: '14D' },
    { title: 'Last Month', value: '30D' },
    { title: 'Last 3 Months', value: '90D' },
    { title: 'Last 6 Months', value: '180D' },
  ];
  activeStatPeriod: any = this.statPeriods[0].value;
  displayStatPeriod: any = this.statPeriods[0].title;

  chartSummaryPeriods: any = [
    { title: 'This Year', value: 'this_year' },
    { title: 'Last Year', value: 'last_year' },
  ];
  activeChartSummaryPeriod: any = this.chartSummaryPeriods[0].value;
  displayChartSummaryPeriod: any = this.chartSummaryPeriods[0].title;

  activeChartSummaryMetric: string = 'orders';

  offers: OfferItem[] = [
    {
      title: 'A popular Indian street food snack',
      description: 'Crispy shells filled with spiced potatoes and chutney.',
      price: '$16.50',
      wasPrice: '$22.00',
      discount: '25% OFF',
      imageUrl: 'https://images.unsplash.com/photo-1601050690597-df0568f70950?w=120&h=120&fit=crop',
    },
    {
      title: 'Weekend brunch combo',
      description: 'Eggs, sourdough, seasonal fruit, and fresh juice.',
      price: '$24.00',
      wasPrice: '$32.00',
      discount: '25% OFF',
      imageUrl: 'https://images.unsplash.com/photo-1533089860892-a7c6f0a88666?w=120&h=120&fit=crop',
    },
    {
      title: 'Chef’s tasting flight',
      description: 'Five small plates paired with house wine.',
      price: '$48.00',
      wasPrice: '$64.00',
      discount: '25% OFF',
      imageUrl: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=120&h=120&fit=crop',
    },
    {
      title: 'Dessert Flight',
      description: 'Three desserts paired with house wine.',
      price: '$24.00',
      wasPrice: '$32.00',
      discount: '25% OFF',
      imageUrl: 'https://images.unsplash.com/photo-1533089860892-a7c6f0a88666?w=120&h=120&fit=crop',
    },
    {
      title: 'Cheesecake Flight',
      description: 'Three cheesecakes paired with house wine.',
      price: '$18.00',
      wasPrice: '$24.00',
      discount: '25% OFF',
      imageUrl: 'https://images.unsplash.com/photo-1533089860892-a7c6f0a88666?w=120&h=120&fit=crop',
    },
  ];

  todaySpecials = [
    {
      name: 'Grilled sea bass',
      note: 'Citrus herb butter · seasonal greens',
      price: '$28',
      imageUrl: 'https://images.unsplash.com/photo-1467003909585-2f8a72700288?w=96&h=96&fit=crop',
    },
    {
      name: 'House-made pasta',
      note: 'Slow-simmered tomato · basil',
      price: '$22',
      imageUrl: 'https://images.unsplash.com/photo-1621996346565-e3dbc353d0e1?w=96&h=96&fit=crop',
    },
  ];

  offerSlideIndex = 0;
  readonly offersPerSlide = 2;
  insightSlideIndex = 0;
  readonly insightsPerSlide = 2;
  private readonly insightSlideIntervalMs = 15000;
  private insightSlideInterval?: ReturnType<typeof setInterval>;

  compactStats: CompactStatCard[] = [
    { title: 'Orders', value: '0', icon: 'bx bx-food-menu', tone: 'dark' },
    { title: 'Revenue', value: '$' + '0', icon: 'bx bx-dollar-circle', tone: 'dark' },
    { title: 'Menus', value: '0', icon: 'bx bx-restaurant', svgIcon: 'dish-icon', tone: 'dark' },
    { title: 'Customer Rating', value: '0', icon: 'bx bx-star', tone: 'dark' },
  ];

  /** True until the first getMenuStats() response — drives the skeleton shimmer
   * for the Stat Overview cards and the compact stats row below them. */
  statsLoading = true;
  skeletonMiniCards: number[] = Array(5).fill(0);
  skeletonCompactCards: number[] = Array(4).fill(0);

  isSuperAdmin: boolean = false;
  dashboardService = inject(DashboardService);
  chatService = inject(ChatService);
  commonChatService = inject(CommonChatService);
  ngxSpinnerService = inject(NgxSpinnerService);
  restaurantService = inject(RestaurantService);
  subscriptionService = inject(SubscriptionService);
  paymentMethod: any = null;
  private router = inject(Router);
  todayQuote: string = '';
  /** Re-triggers quote fade-in after API loads */
  quoteAnimated = false;
  user: any;

  /** e.g. "Good morning, Jane!" based on local time */
  get greetingTitle(): string {
    const name = this.user?.first_name ?? this.userName ?? 'there';
    return `${this.getTimeOfDayGreeting()}, ${name}!`;
  }

  private getTimeOfDayGreeting(): string {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) {
      return 'Good Morning';
    }
    if (hour >= 12 && hour < 17) {
      return 'Good Afternoon';
    }
    return 'Good Evening';
  }

  period: string = '14D';
  periods: any[] = ['7D', '14D', '30D', '90D', '180D'];
  chartOrderSummary: any = [];
  menuStats: any = [];
  currentRestaurant: any;
  /**
   * The Hardware page (where Fork & Find / Square / Clover are chosen) is
   * behind the HARDWARE flag and redirects home when it is off; the no-POS
   * banner then enables Fork & Find directly instead of linking there.
   */
  canOpenHardware = false;
  enablingFnf = false;
  chatCursor = '';
  chatLimit = 20;
  chatHistoryLoading = false;
  chatMessagesLoadingMore = false;
  private chatStreamSub?: Subscription;
  private streamReplyPollTimer?: ReturnType<typeof setTimeout>;
  private activeStreamInsightId: string | null = null;
  private activeCommonChatId: string | null = null;
  subscriptionDetails: any = null;
  currentPlanName: string | null = null;

  /** No POS chosen yet (`pos_provider: 'none'`): ordering stays off until
   * the owner enables Fork & Find or connects Square / Clover. */
  get hasNoPosProvider(): boolean {
    return isPosNotSelected(this.currentRestaurant?.pos_provider);
  }

  /** The banner's button when the Hardware page is not reachable. */
  onEnableForkAndFind() {
    if (this.enablingFnf) return;
    this.enablingFnf = true;
    this.restaurantService.enableForkAndFind(this.currentRestaurant).subscribe({
      next: (response: FNFResponse<any>) => {
        this.enablingFnf = false;
        if (!response?.data && response?.error) {
          this.toastr.error(
            response.error.error?.message ||
              response.error.message ||
              'Unable to enable Fork & Find.',
            'Error',
          );
          return;
        }
        this.toastr.success(
          'Fork & Find enabled. Our support team will activate your restaurant within 1-2 working days.',
          'Success',
        );
      },
      error: () => {
        this.enablingFnf = false;
        this.toastr.error('Unable to enable Fork & Find.', 'Error');
      },
    });
  }

  constructor(private toastr: ToastrService) {
    if (localStorage.getItem('user')) {
      let user: any = localStorage.getItem('user');
      if (user) {
        this.user = JSON.parse(user);
      }
    }
    if (this.user && (this.user.restaurant_id == 'super_admin' || !this.user.restaurant_id)) {
      this.isSuperAdmin = true;
      localStorage.removeItem('userToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      this.router.navigate(['login']);
      return;
    }
    this.restaurantService.currentRestaurantDetails$.subscribe((restaurant: any) => {
      this.currentRestaurant = restaurant;
      this.restaurantName = this.currentRestaurant?.restaurant_name;
    });
    this.restaurantService.restaurantFeatureFlags$.subscribe((flags: FeatureFlag[] | null) => {
      this.canOpenHardware = isFeatureActive(flags, FEATURE_FLAG_KEYS.HARDWARE);
    });
    this.restaurantService.restaurantSubscription$.subscribe((subscription: any) => {
      if (!subscription) {
        return;
      }
      this.subscriptionDetails = subscription;
    });
    this.restaurantService.currentPlanName$.subscribe((planName: string | null) => {
      this.currentPlanName = planName;
    });
    this.restaurantService.restaurantPaymentMethod$.subscribe((paymentMethod: any) => {
      if (!paymentMethod) {
        return;
      }
      this.paymentMethod = paymentMethod;
    });
  }

  ngOnInit(): void {
    this.restoreViewFromUrl();
    this.loadChatPanelWidth();
    this.applyChartTheme();

    if (this.isSuperAdmin) {
      return;
    }
    if (typeof localStorage === 'undefined') return;
    const raw = localStorage.getItem('user');
    if (!raw) return;
    try {
      const u = JSON.parse(raw) as {
        first_name?: string;
        name?: string;
        restaurant_name?: string;
      };
      if (u.first_name) this.userName = u.first_name;
      else if (u.name) this.userName = u.name.split(' ')[0] ?? this.userName;
      if (u.restaurant_name) this.restaurantName = u.restaurant_name;
    } catch {
      /* keep defaults */
    }

    this.getTodayQuote();
    this.getChartOrderSummary();
    this.getMenuStats();
    this.getAiInsights();
  }

  ngOnDestroy(): void {
    this.endChatPanelResize();
    this.stopInsightSlideAutoRotate();
    this.stopChatStream();
    this.stopCommonChatStream();
    this.unlockBodyScroll();
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.closeChatListMenu();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.editingChatTitleId) {
      this.cancelEditingChatTitle();
    } else if (this.openChatListMenuId) {
      this.closeChatListMenu();
    } else if (this.chatPanelOpen) {
      this.closeChatPanel();
    } else if (this.chatListPanelOpen) {
      this.closeChatListPanel();
    }
  }

  setSummaryTab(tab: string): void {
    this.applySummaryTab(tab);
    this.urlState.set('tab', tab.toLowerCase(), this.summaryTabs[0].toLowerCase());
    this.getChartOrderSummary();
  }

  private applySummaryTab(tab: string): void {
    this.activeSummaryTab = tab;
    this.activeChartSummaryMetric = this.summaryMetrics[tab] ?? 'orders';
  }

  /** The periods and Summary tab the URL asks for, if valid. */
  private restoreViewFromUrl(): void {
    const stats = this.statPeriods.find((p: any) => p.value === this.urlState.get('stats'));
    if (stats) {
      this.activeStatPeriod = stats.value;
      this.displayStatPeriod = stats.title;
    }
    const summary = this.chartSummaryPeriods.find(
      (p: any) => p.value === this.urlState.get('summary'),
    );
    if (summary) {
      this.activeChartSummaryPeriod = summary.value;
      this.displayChartSummaryPeriod = summary.title;
    }
    const tab = this.summaryTabs.find((t) => t.toLowerCase() === this.urlState.get('tab'));
    if (tab) this.applySummaryTab(tab);
  }

  get canStartChat(): boolean {
    return !!this.chatInput?.trim();
  }

  startChat(): void {
    this.setPanelMessages([]);
    const seed = this.chatInput.trim();
    if (!seed) {
      return;
    }
    this.chatPanelSubtitle = seed || null;
    this.chatPanelName = null;
    this.chatPanelInsightId = null;
    this.chatPanelInsightValue = null;
    // this.panelMessages = [
    //   {
    //     role: 'user',
    //     text: seed
    //       ? `${seed}`
    //       : `Hi! Ask me about menus, offers, and ways to grow revenue for ${this.restaurantName}.`,
    //     createdAt: new Date().toISOString(),
    //   },
    // ];
    this.panelComposer = '';
    // this.chatInput = '';
    // this.openChatPanel();
    this.stopChatStream();
    this.stopCommonChatStream();
    this.initiateCommonChat();
  }

  onChatInsight(index: number): void {
    const row = this.insights[index];
    if (!row) return;
    // const full = `${row.insight} ${row.insightValue}`;
    const full = `${row.insight}`;
    // this.chatPanelSubtitle = full.length > 120 ? `${full.slice(0, 120)}…` : full;
    this.chatPanelSubtitle = full;
    this.chatPanelName = row.name;
    this.chatPanelInsightId = row.insightID;
    this.chatPanelInsightValue = row.insightValue;
    this.chatPanelChatId = '';
    this.stopCommonChatStream();
    this.setPanelMessages([]);
    this.chatCursor = '';
    this.panelComposer = '';
    this.initiateChat();
  }

  initiateChat(): void {
    this.dashboardService
      .initiateChat(this.currentRestaurant.restaurant_id, this.chatPanelInsightId)
      .subscribe((response: any) => {
        if (!response.data || response.error) {
          console.log(response.error.error?.message || response.error.message);
          this.toastr.error('Unable to initiate chat. Please try again later.');
          return;
        } else if (response.data) {
          this.chatPanelChatId = response.data.chatId;
          this.chatInput = '';
          this.openChatPanel();
          this.getExistingChatMessages(false);
          this.startChatStream();
        }
      });
  }

  initiateCommonChat(): void {
    this.dashboardService
      .initiateCommonChat(this.currentRestaurant.restaurant_id)
      .subscribe((response: any) => {
        if (!response.data || response.error) {
          console.log(response.error.error?.message || response.error.message);
          this.toastr.error('Unable to initiate chat. Please try again later.');
          return;
        } else if (response.data) {
          const chatId = response.data.chatId ?? response.data.chat_id ?? response.data.id ?? '';
          if (!chatId) {
            this.toastr.error('Unable to start chat session. Please try again.');
            return;
          }
          this.chatPanelChatId = chatId;
          this.openChatPanel();
          this.startCommonChatStream();
          this.sendCommonChatPanelMessage();
        }
      });
  }

  sendCommonChatPanelMessage(): void {
    const text = this.chatInput.trim();
    if (!text) return;
    this.chatInput = '';
    this.panelComposer = '';
    this.startCommonChatStream();
    const now = utcNowIso();
    this.setPanelMessages([
      ...this.panelMessages,
      { role: 'user', text, createdAt: now },
      {
        role: 'assistant',
        text: 'Thinking',
        loading: true,
        createdAt: now,
      },
    ]);
    this.scrollChatToBottom();

    // POST only triggers the agent; assistant reply is shown via WebSocket stream.
    this.dashboardService
      .chatWithCommonAgent(this.currentRestaurant.restaurant_id, this.chatPanelChatId, {
        text: text,
      })
      .subscribe({
        next: (response: FNFResponse<any>) => {
          if (!response.data && response.error) {
            this.clearStreamReplyPoll();
            this.removeLoadingMessage();
            console.log(response.error.error?.message || response.error.message);
            this.appendAssistantMessage(
              response.error.error?.message ||
                response.error.message ||
                'Something went wrong. Please try again.',
            );
          } else {
            this.scheduleStreamReplyPoll();
          }
        },
        error: () => {
          this.clearStreamReplyPoll();
          this.removeLoadingMessage();
          console.log('Unable to complete your action. Please try again later.');
          this.appendAssistantMessage('Unable to complete your action. Please try again later.');
        },
      });
  }

  get hasMoreChatMessages(): boolean {
    return !!this.chatCursor?.trim();
  }

  loadMoreChatMessages(): void {
    if (!this.hasMoreChatMessages || this.chatMessagesLoadingMore) {
      return;
    }
    this.getExistingChatMessages(true);
  }

  openChatPanel(): void {
    this.chatPanelOpen = true;
    this.lockBodyScroll();
    this.scrollChatToBottom();
  }

  closeChatPanel(): void {
    this.stopChatStream();
    this.stopCommonChatStream();
    this.chatOpenedFromHistory = false;
    this.chatPanelOpen = false;
    this.chatCursor = '';
    this.chatInput = '';
    this.chatPanelChatId = '';
    this.chatPanelInsightValue = null;
    this.chatHistoryLoading = false;
    this.chatMessagesLoadingMore = false;
    this.unlockBodyScroll();
  }

  onChatPanelResizeStart(event: PointerEvent): void {
    if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.chatPanelResizing = true;
    this.chatPanelResizeStartX = event.clientX;
    this.chatPanelResizeStartWidth = this.chatPanelWidth;
    this.doc.body.style.cursor = 'col-resize';
    this.doc.body.style.userSelect = 'none';
  }

  @HostListener('document:pointermove', ['$event'])
  onChatPanelResizeMove(event: PointerEvent): void {
    if (!this.chatPanelResizing) {
      return;
    }

    event.preventDefault();
    const delta = this.chatPanelResizeStartX - event.clientX;
    this.chatPanelWidth = this.clampChatPanelWidth(this.chatPanelResizeStartWidth + delta);
  }

  @HostListener('document:pointerup')
  @HostListener('document:pointercancel')
  onChatPanelResizeEnd(): void {
    this.endChatPanelResize();
  }

  private endChatPanelResize(): void {
    if (!this.chatPanelResizing) {
      return;
    }

    this.chatPanelResizing = false;
    this.doc.body.style.cursor = '';
    this.doc.body.style.userSelect = '';
    this.persistChatPanelWidth();
  }

  private loadChatPanelWidth(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }

    const raw = localStorage.getItem(Dashboard2Component.CHAT_PANEL_WIDTH_STORAGE_KEY);
    if (!raw) {
      return;
    }

    const parsed = Number.parseInt(raw, 10);
    if (!Number.isNaN(parsed)) {
      this.chatPanelWidth = this.clampChatPanelWidth(parsed);
    }
  }

  private persistChatPanelWidth(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }

    localStorage.setItem(
      Dashboard2Component.CHAT_PANEL_WIDTH_STORAGE_KEY,
      String(this.chatPanelWidth),
    );
  }

  private clampChatPanelWidth(width: number): number {
    const maxAllowed = Math.min(this.chatPanelMaxWidth, window.innerWidth);
    const minAllowed = Math.min(this.chatPanelMinWidth, maxAllowed);
    return Math.min(Math.max(width, minAllowed), maxAllowed);
  }

  sendPanelMessage(): void {
    const text = this.panelComposer.trim();
    if (!text) return;
    this.panelComposer = '';
    const now = utcNowIso();
    this.setPanelMessages([
      ...this.panelMessages,
      { role: 'user', text, createdAt: now },
      {
        role: 'assistant',
        text: 'Thinking',
        loading: true,
        createdAt: now,
      },
    ]);
    this.scrollChatToBottom();

    const restaurantId = this.currentRestaurant.restaurant_id;
    const isCommonChat = !!this.chatPanelChatId && !this.chatPanelInsightId;
    if (isCommonChat) {
      this.startCommonChatStream();
    } else {
      this.startChatStream();
    }

    const post$ = isCommonChat
      ? this.dashboardService.chatWithCommonAgent(restaurantId, this.chatPanelChatId, {
          text,
        })
      : this.dashboardService.chatWitAgent({ text }, restaurantId, this.chatPanelInsightId!);

    // POST only triggers the agent; assistant reply is shown via WebSocket stream.
    post$.subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.clearStreamReplyPoll();
          this.removeLoadingMessage();
          console.log(response.error.error?.message || response.error.message);
          this.appendAssistantMessage(
            response.error.error?.message ||
              response.error.message ||
              'Something went wrong. Please try again.',
          );
        } else {
          this.scheduleStreamReplyPoll();
        }
      },
      error: () => {
        this.clearStreamReplyPoll();
        this.removeLoadingMessage();
        console.log('Unable to complete your action. Please try again later.');
        this.appendAssistantMessage('Unable to complete your action. Please try again later.');
      },
    });
  }

  private setPanelMessages(messages: ChatPanelMessage[]): void {
    this.panelMessages = buildMessagesHtmlCache(messages, this.sanitizer);
  }

  private removeLoadingMessage(): void {
    this.setPanelMessages(this.panelMessages.filter((message) => !message.loading));
  }

  private appendAssistantMessage(text: string, role: 'user' | 'assistant' = 'assistant'): void {
    this.setPanelMessages([...this.panelMessages, { role: role, text, createdAt: utcNowIso() }]);
    this.scrollChatToBottom();
  }

  private startChatStream(): void {
    const restaurantId = this.currentRestaurant?.restaurant_id;
    const insightId = this.chatPanelInsightId;
    if (!restaurantId || !insightId) {
      return;
    }

    if (
      this.activeStreamInsightId === insightId &&
      (this.chatService.isActive || this.chatStreamSub)
    ) {
      return;
    }

    this.stopChatStream();
    this.activeStreamInsightId = insightId;
    this.chatStreamSub = this.chatService
      .connect(restaurantId, insightId)
      .subscribe((event) => this.handleChatStreamEvent(event));
  }

  private stopChatStream(): void {
    this.clearStreamReplyPoll();
    this.activeStreamInsightId = null;
    this.chatStreamSub?.unsubscribe();
    this.chatStreamSub = undefined;
    this.chatService.disconnect();
  }

  private startCommonChatStream(): void {
    const restaurantId = this.currentRestaurant?.restaurant_id;
    const chatId = this.chatPanelChatId?.trim();
    if (!restaurantId || !chatId) {
      return;
    }

    if (
      this.activeCommonChatId === chatId &&
      (this.commonChatService.isActive || this.chatStreamSub)
    ) {
      return;
    }

    this.stopCommonChatStream();
    this.stopChatStream();
    this.activeCommonChatId = chatId;
    this.chatStreamSub = this.commonChatService
      .connect(restaurantId, chatId)
      .subscribe((event) => this.handleChatStreamEvent(event));
  }

  private stopCommonChatStream(): void {
    this.clearStreamReplyPoll();
    this.activeCommonChatId = null;
    this.chatStreamSub?.unsubscribe();
    this.chatStreamSub = undefined;
    this.commonChatService.disconnect();
  }

  private scheduleStreamReplyPoll(): void {
    this.clearStreamReplyPoll();
    this.streamReplyPollTimer = setTimeout(() => this.pollForStreamedReply(0), 10000);
  }

  private clearStreamReplyPoll(): void {
    if (this.streamReplyPollTimer) {
      clearTimeout(this.streamReplyPollTimer);
      this.streamReplyPollTimer = undefined;
    }
  }

  /** Fallback when WebSocket/SSE stream is unavailable — fetch latest assistant reply. */
  private pollForStreamedReply(attempt: number): void {
    const maxAttempts = 10;
    if (attempt >= maxAttempts) {
      return;
    }

    const stillLoading = this.panelMessages.some((message) => message.loading);
    if (!stillLoading) {
      return;
    }

    const restaurantId = this.currentRestaurant?.restaurant_id;
    const insightId = this.chatPanelInsightId;
    const chatId = this.chatPanelChatId;
    const isCommonChat = !!chatId && !insightId;
    if (!restaurantId || (!insightId && !isCommonChat)) {
      return;
    }

    const messages$ = isCommonChat
      ? this.dashboardService.getCommonChatMessages(restaurantId, chatId, '', this.chatLimit)
      : this.dashboardService.getChatMessages(restaurantId, insightId!, '', this.chatLimit);

    messages$.subscribe({
      next: (response: any) => {
        const { data } = this.normalizeChatMessagesApiBody(response);
        const mapped = this.mapApiChatMessages(data?.messages ?? []);
        const latestAssistant = [...mapped]
          .reverse()
          .find((message) => message.role === 'assistant' && message.text.trim());

        if (latestAssistant) {
          this.applyStreamAssistantMessage(
            {
              type: 'message',
              content: latestAssistant.text,
              messageId: latestAssistant.id,
            },
            latestAssistant.tokens,
          );
          return;
        }

        this.streamReplyPollTimer = setTimeout(() => this.pollForStreamedReply(attempt + 1), 1500);
      },
      error: () => {
        this.streamReplyPollTimer = setTimeout(() => this.pollForStreamedReply(attempt + 1), 1500);
      },
    });
  }

  private handleChatStreamEvent(event: ChatStreamEvent): void {
    if (event.type === 'token' || event.type === 'message' || event.type === 'done') {
      this.clearStreamReplyPoll();
    }

    switch (event.type) {
      case 'token':
        this.handleStreamToken(event.content ?? '');
        break;
      case 'message':
        this.applyStreamAssistantMessage(event);
        break;
      case 'done':
        this.finalizeStreamedAssistant();
        break;
      case 'chat_title_updated':
        this.applyChatTitleUpdated(event.content ?? '');
        break;
      case 'error':
        break;
      default:
        break;
    }
  }

  private handleStreamToken(chunk: string): void {
    if (!chunk) {
      return;
    }

    const messages = [...this.panelMessages];
    let loadingIndex = messages.findIndex((message) => message.loading);

    if (loadingIndex < 0) {
      messages.push({
        role: 'assistant',
        text: chunk,
        loading: true,
        createdAt: utcNowIso(),
      });
      this.panelMessages = [...messages];
      this.scrollChatToBottom();
      return;
    }

    const current = messages[loadingIndex];
    const nextText = current.text === 'Thinking' ? chunk : `${current.text}${chunk}`;
    messages[loadingIndex] = {
      ...current,
      text: nextText,
      loading: true,
    };
    this.panelMessages = [...messages];
    this.scrollChatToBottom();
  }

  private applyStreamAssistantMessage(
    event: ChatStreamEvent,
    tokensOverride?: ChatMetricToken[],
  ): void {
    const text = (event.content ?? this.extractStreamContentFromData(event.data))?.trim();
    if (!text) {
      return;
    }

    if (event.messageId && this.panelMessages.some((m) => m.id === event.messageId)) {
      return;
    }

    const messages = [...this.panelMessages];
    const loadingIndex = messages.findIndex((message) => message.loading);
    const assistantMessage: ChatPanelMessage = {
      role: 'assistant',
      text,
      tokens: tokensOverride ?? extractTokensFromPayload(event.data),
      id: event.messageId,
      createdAt: utcNowIso(),
      loading: false,
    };

    if (loadingIndex >= 0) {
      messages[loadingIndex] = assistantMessage;
      this.setPanelMessages(messages);
    } else {
      this.setPanelMessages([...messages, assistantMessage]);
    }
    this.scrollChatToBottom();
  }

  private finalizeStreamedAssistant(): void {
    const hasLoading = this.panelMessages.some((message) => message.loading);
    if (!hasLoading) {
      return;
    }

    this.setPanelMessages(
      this.panelMessages.map((message) =>
        message.loading ? { ...message, loading: false } : message,
      ),
    );
    this.scrollChatToBottom();
  }

  /** WebSocket `chat_title_updated` — replace seed/first-message subtitle with server title. */
  private applyChatTitleUpdated(title: string): void {
    const trimmed = title.trim();
    if (!trimmed) {
      return;
    }

    this.chatPanelSubtitle = trimmed;

    const chatId = this.chatPanelChatId?.trim();
    if (!chatId) {
      return;
    }

    this.allChats = this.allChats.map((chat) =>
      chat.chatId === chatId ? { ...chat, title: trimmed } : chat,
    );
  }

  private extractStreamContentFromData(data: unknown): string | undefined {
    if (!data || typeof data !== 'object') {
      return undefined;
    }

    const record = data as Record<string, unknown>;
    const nested = record['data'] as Record<string, unknown> | undefined;
    const candidates = [
      record['content'],
      record['text'],
      record['message'],
      nested?.['content'],
      nested?.['text'],
      nested?.['message'],
    ];

    for (const value of candidates) {
      if (typeof value === 'string' && value.trim()) {
        return value;
      }
    }

    const messageObj = record['message'];
    if (messageObj && typeof messageObj === 'object') {
      const msg = messageObj as Record<string, unknown>;
      if (typeof msg['content'] === 'string') {
        return msg['content'];
      }
    }

    return undefined;
  }

  private scrollChatToBottom(): void {
    this.chatPanelMessages?.scrollToBottom();
  }

  onPanelComposerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendPanelMessage();
    }
  }

  private lockBodyScroll(): void {
    this.doc.body.style.overflow = 'hidden';
  }

  private unlockBodyScroll(): void {
    this.doc.body.style.overflow = '';
  }

  setOfferSlide(i: number): void {
    const maxIndex = Math.max(this.offerSlidePages.length - 1, 0);
    this.offerSlideIndex = Math.min(Math.max(i, 0), maxIndex);
  }

  setInsightSlide(i: number): void {
    const maxIndex = Math.max(this.insightSlidePages.length - 1, 0);
    this.insightSlideIndex = Math.min(Math.max(i, 0), maxIndex);
    this.startInsightSlideAutoRotate();
  }

  private startInsightSlideAutoRotate(): void {
    this.stopInsightSlideAutoRotate();

    if (this.insightSlidePages.length <= 1) {
      return;
    }

    this.insightSlideInterval = setInterval(() => {
      const pageCount = this.insightSlidePages.length;
      if (pageCount <= 1) {
        this.stopInsightSlideAutoRotate();
        return;
      }
      this.insightSlideIndex = (this.insightSlideIndex + 1) % pageCount;
    }, this.insightSlideIntervalMs);
  }

  private stopInsightSlideAutoRotate(): void {
    if (this.insightSlideInterval) {
      clearInterval(this.insightSlideInterval);
      this.insightSlideInterval = undefined;
    }
  }

  get visibleOffers(): OfferItem[] {
    const start = this.offerSlideIndex * this.offersPerSlide;
    return this.offers.slice(start, start + this.offersPerSlide);
  }

  get offerSlidePages(): number[] {
    const pages = Math.ceil(this.offers.length / this.offersPerSlide);
    return Array.from({ length: pages }, (_, i) => i);
  }

  get visibleInsights(): InsightRow[] {
    const start = this.insightSlideIndex * this.insightsPerSlide;
    return this.insights.slice(start, start + this.insightsPerSlide);
  }

  get insightSlidePages(): number[] {
    const pages = Math.ceil(this.insights.length / this.insightsPerSlide);
    return Array.from({ length: pages }, (_, i) => i);
  }

  getTodayQuote() {
    this.quoteAnimated = false;
    this.dashboardService.getTodayQuote().subscribe((response: any) => {
      this.todayQuote = response.data ?? '';
      this.quoteAnimated = false;
      requestAnimationFrame(() => {
        this.quoteAnimated = !!this.todayQuote;
      });
    });
  }

  getChartOrderSummary() {
    this.dashboardService
      .getChartOrderSummary(this.activeChartSummaryPeriod, this.activeChartSummaryMetric)
      .subscribe((response: any) => {
        const payload = response?.data;
        this.chartOrderSummary = payload;
        const raw = payload?.data;
        if (!Array.isArray(raw)) {
          this.barChartData = { labels: [], datasets: [{ data: [], label: '' }] };
          return;
        }
        this.applySummaryChartData(raw);
      });
  }

  /**
   * Chart.js paints to a canvas, so it cannot read the theme tokens the rest
   * of the app uses — the palette has to be resolved from the body at build
   * time instead. Falls back to the light-mode literals.
   */
  private chartPalette(): { bar: string; tick: string; grid: string; brand: string } {
    const cs = getComputedStyle(document.body);
    const token = (name: string, fallback: string) => cs.getPropertyValue(name).trim() || fallback;
    return {
      bar: token('--c-border', '#e2e8f0'),
      tick: token('--c-text-2', '#6c7689'),
      grid: token('--c-border', '#e8eaed'),
      // The chart draws to canvas, so the token has to be resolved here
      // rather than left to CSS.
      brand: token('--c-brand', '#b31d2c'),
    };
  }

  private applyChartTheme(): void {
    const p = this.chartPalette();
    const scales = this.barChartOptions.scales as any;
    if (scales?.x?.ticks) scales.x.ticks.color = p.tick;
    if (scales?.y?.ticks) scales.y.ticks.color = p.tick;
    if (scales?.y?.grid) scales.y.grid.color = p.grid;
    const ds = this.barChartData.datasets?.[0] as any;
    if (ds) ds.hoverBackgroundColor = p.brand;
  }

  private applySummaryChartData(
    raw: { month: string; value: number | string; highlight?: boolean }[],
  ): void {
    const numeric = raw.map((x) => ({
      month: x.month,
      value: Number(x.value) || 0,
      highlight: !!x.highlight,
    }));

    const palette = this.chartPalette();
    this.barChartData = {
      labels: numeric.map((x) => x.month),
      datasets: [
        {
          data: numeric.map((x) => x.value),
          backgroundColor: numeric.map((x) => (x.highlight ? palette.brand : palette.bar)),
          hoverBackgroundColor: palette.brand,
          borderRadius: 4,
          borderSkipped: false,
          label: this.activeSummaryTab,
        },
      ],
    };
  }

  getMenuStats() {
    this.statsLoading = true;
    this.dashboardService.getMenuStats(this.activeStatPeriod).subscribe((response: any) => {
      this.statsLoading = false;
      this.menuStats = response.data;
      this.compactStats.forEach((card: any) => {
        if (card.title === 'Orders') {
          card.value = this.menuStats?.find((e: any) => e.title === 'Total Orders')?.value || 0;
        } else if (card.title === 'Revenue') {
          card.value =
            '$' + (this.menuStats?.find((e: any) => e.title === 'Total Revenue')?.value || 0);
        } else if (card.title === 'Menus') {
          card.value = this.menuStats?.find((e: any) => e.title === 'Published Menus')?.value || 0;
        } else if (card.title === 'Customer Rating') {
          card.value = this.menuStats?.find((e: any) => e.title === 'Customer Ratings')?.value || 0;
        }
      });

      this.statCards.forEach((card: any) => {
        if (card.category === 'Highest Priced Item') {
          let highestPricedItem = this.menuStats?.find(
            (e: any) => e.title === 'Highest Priced Item',
          );
          card.salesText = (highestPricedItem?.value || 0) + ' Sales';
          card.salesTone =
            highestPricedItem?.sentiment === 1
              ? 'success'
              : highestPricedItem?.sentiment === -1
                ? 'danger'
                : 'default';
          card.itemName = highestPricedItem?.menu?.name || 'Coming Soon ...';
          card.imageUrl = highestPricedItem?.menu?.thumbnail_url || '';
        } else if (card.category === 'Most Ordered Item') {
          let mostOrderedItem = this.menuStats?.find(
            (e: any) => e.title === 'Highest Ordered Item',
          );
          card.salesText = (mostOrderedItem?.value || 0) + ' Sales';
          card.salesTone =
            mostOrderedItem?.sentiment === 1
              ? 'success'
              : mostOrderedItem?.sentiment === -1
                ? 'danger'
                : 'default';
          card.itemName = mostOrderedItem?.menu?.name || 'Coming Soon ...';
          card.imageUrl = mostOrderedItem?.menu?.thumbnail_url || '';
        } else if (card.category === 'Least Ordered Item') {
          let leastOrderedItem = this.menuStats?.find((e: any) => e.title === 'Least Ordered Item');
          card.salesText = (leastOrderedItem?.value || 0) + ' Sales';
          card.salesTone =
            leastOrderedItem?.sentiment === 1
              ? 'success'
              : leastOrderedItem?.sentiment === -1
                ? 'danger'
                : 'default';
          card.itemName = leastOrderedItem?.menu?.name || 'Coming Soon ...';
          card.imageUrl = leastOrderedItem?.menu?.thumbnail_url || '';
        } else if (card.category === 'Customer Favorite Item') {
          let customerFavoriteItem = this.menuStats?.find(
            (e: any) => e.title === 'Customer Favorite Item',
          );
          card.salesText = (customerFavoriteItem?.value || 0) + ' Sales';
          card.salesTone =
            customerFavoriteItem?.sentiment === 1
              ? 'success'
              : customerFavoriteItem?.sentiment === -1
                ? 'danger'
                : 'default';
          card.itemName = customerFavoriteItem?.menu?.name || 'Coming Soon ...';
          card.imageUrl = customerFavoriteItem?.menu?.thumbnail_url || '';
        } else if (card.category === 'Least Favorite Item') {
          let leastFavoriteItem = this.menuStats?.find(
            (e: any) => e.title === 'Least Favorite Item',
          );
          card.salesText = (leastFavoriteItem?.value || 0) + ' Sales';
          card.salesTone =
            leastFavoriteItem?.sentiment === 1
              ? 'success'
              : leastFavoriteItem?.sentiment === -1
                ? 'danger'
                : 'default';
          card.itemName = leastFavoriteItem?.menu?.name || 'Coming Soon ...';
          card.imageUrl = leastFavoriteItem?.menu?.thumbnail_url || '';
        }
      });
    });
  }

  onChangeStatPeriod(period: any) {
    this.activeStatPeriod = period.value;
    this.displayStatPeriod = period.title;
    this.urlState.set('stats', period.value, this.statPeriods[0].value);
    this.getMenuStats();
  }

  onChangeChartSummaryPeriod(period: any) {
    this.activeChartSummaryPeriod = period.value;
    this.displayChartSummaryPeriod = period.title;
    this.urlState.set('summary', period.value, this.chartSummaryPeriods[0].value);
    this.getChartOrderSummary();
  }

  /** Set when a card has no photo, or its photo fails to load. */
  private readonly missingThumbs = new Set<string>();

  /**
   * True when a card should show the themed dish glyph instead of a photo.
   * An <img> pointing at an SVG is an isolated document, so it cannot pick
   * up currentColor — the placeholder has to be rendered inline to theme.
   */
  isThumbMissing(card: { category: string; imageUrl: string }): boolean {
    return (
      this.missingThumbs.has(card.category) ||
      !card.imageUrl ||
      card.imageUrl.includes('menu-thump')
    );
  }

  onImageError(card: { category: string }): void {
    this.missingThumbs.add(card.category);
  }

  getAiInsights() {
    this.insightsLoading = true;
    this.dashboardService.getAiInsights().subscribe({
      next: (response: any) => {
        this.insights = response.data?.insights || [];
        this.allInsightsHaveValue = this.insights.every(
          (insight: any) => insight.insightValue !== null && insight.insightValue !== '',
        );
        this.insightSlideIndex = 0;
        this.insightsLoading = false;
        this.startInsightSlideAutoRotate();
      },
      error: () => {
        this.insights = [];
        this.insightsLoading = false;
        this.stopInsightSlideAutoRotate();
      },
    });
  }

  getExistingChatMessages(loadOlder = false): void {
    const restaurantId = this.currentRestaurant?.restaurant_id;
    const isInsightChat = !!this.chatPanelInsightId;
    const isCommonChat = !!this.chatPanelChatId && !this.chatPanelInsightId;
    if (!restaurantId || (!isInsightChat && !isCommonChat)) {
      return;
    }

    const cursor = loadOlder ? this.chatCursor : '';

    if (loadOlder) {
      this.chatMessagesLoadingMore = true;
    } else {
      this.chatHistoryLoading = true;
    }

    const messages$ = isCommonChat
      ? this.dashboardService.getCommonChatMessages(
          restaurantId,
          this.chatPanelChatId,
          cursor,
          this.chatLimit,
        )
      : this.dashboardService.getChatMessages(
          restaurantId,
          this.chatPanelInsightId!,
          cursor,
          this.chatLimit,
        );

    messages$.subscribe({
      next: (response: any) => {
        const { data, apiError } = this.normalizeChatMessagesApiBody(response);

        if (apiError || !data) {
          if (!loadOlder && isInsightChat) {
            this.setPanelMessagesToSelectedInsight();
            this.chatCursor = '';
          } else if (!loadOlder) {
            this.setPanelMessages([]);
            this.chatCursor = '';
          }
          return;
        }

        const mapped = this.mapApiChatMessages(data.messages ?? []);

        if (loadOlder) {
          this.mergeOlderChatMessages(mapped);
        } else if (mapped.length > 0) {
          this.setPanelMessages(mapped);
          this.scrollChatToBottom();
        } else if (isInsightChat) {
          this.setPanelMessagesToSelectedInsight();
        } else {
          this.setPanelMessages([]);
        }

        this.chatCursor = data.nextCursor ?? '';
      },
      error: () => {
        if (!loadOlder && isInsightChat) {
          this.setPanelMessagesToSelectedInsight();
        } else if (!loadOlder) {
          this.setPanelMessages([]);
        }
      },
      complete: () => {
        this.chatHistoryLoading = false;
        this.chatMessagesLoadingMore = false;
      },
    });
  }

  private normalizeChatMessagesApiBody(response: any): {
    data: any | null;
    apiError: { code?: number; message?: string } | null;
  } {
    if (response?.status >= 400 && response?.error && typeof response.error === 'object') {
      const body = response.error;
      return {
        data: body.data ?? null,
        apiError: body.error ?? null,
      };
    }

    return {
      data: response?.data ?? null,
      apiError: response?.error ?? null,
    };
  }

  private setPanelMessagesToSelectedInsight(): void {
    const text = this.chatPanelInsightValue?.trim();
    if (!text) {
      this.setPanelMessages([]);
      return;
    }

    this.setPanelMessages([
      {
        role: 'assistant',
        text,
        createdAt: utcNowIso(),
      },
    ]);
    this.scrollChatToBottom();
  }

  private mapApiChatMessages(apiMessages: any[]): ChatPanelMessage[] {
    const mapped: ChatPanelMessage[] = [];

    for (const message of apiMessages) {
      if (message.role === 'tool_debug') {
        console.log('[tool_debug]', message);
        continue;
      }

      mapped.push({
        id: message.messageId ?? undefined,
        role: message.role === 'user' ? 'user' : 'assistant',
        text: message.content ?? '',
        tokens: normalizeApiTokens(message.tokens),
        createdAt: message.createdAt ?? '',
      });
    }

    return this.sortChatMessages(mapped);
  }

  private sortChatMessages(messages: ChatPanelMessage[]): ChatPanelMessage[] {
    return [...messages].sort((a, b) => {
      const timeDiff = this.getChatMessageTime(a) - this.getChatMessageTime(b);
      if (timeDiff !== 0) {
        return timeDiff;
      }
      return (a.id ?? '').localeCompare(b.id ?? '');
    });
  }

  private getChatMessageTime(message: ChatPanelMessage): number {
    if (!message.createdAt) {
      return 0;
    }
    const parsed = Date.parse(message.createdAt);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private dedupeChatMessages(messages: ChatPanelMessage[]): ChatPanelMessage[] {
    const seen = new Set<string>();
    const unique: ChatPanelMessage[] = [];

    for (const message of messages) {
      const key = message.id ?? `${message.role}|${message.createdAt ?? ''}|${message.text}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      unique.push(message);
    }

    return unique;
  }

  private mergeOlderChatMessages(olderMessages: ChatPanelMessage[]): void {
    if (!olderMessages.length) {
      return;
    }

    const el = this.chatPanelMessages?.getScrollHost();
    const prevScrollHeight = el?.scrollHeight ?? 0;
    const prevScrollTop = el?.scrollTop ?? 0;

    this.setPanelMessages(
      this.sortChatMessages(this.dedupeChatMessages([...olderMessages, ...this.panelMessages])),
    );

    queueMicrotask(() => {
      requestAnimationFrame(() => {
        const host = this.chatPanelMessages?.getScrollHost();
        if (!host) {
          return;
        }
        const heightAdded = host.scrollHeight - prevScrollHeight;
        host.scrollTop = prevScrollTop + heightAdded;
      });
    });
  }

  get hasMoreChats(): boolean {
    return !!this.chatsCursor?.trim();
  }

  getAllChats(): void {
    this.openChatListPanel();
    this.fetchAllChats(false);
  }

  openChatListPanel(): void {
    this.closeChatPanel();
    this.chatListPanelOpen = true;
    this.lockBodyScroll();
  }

  backToChatHistory(): void {
    this.openChatListPanel();
  }

  closeChatListPanel(): void {
    this.chatListPanelOpen = false;
    this.closeChatListMenu();
    this.unlockBodyScroll();
  }

  loadMoreChats(): void {
    if (!this.hasMoreChats || this.chatsLoadingMore || this.isChatTitleEditActive()) {
      return;
    }
    this.fetchAllChats(true);
  }

  private fetchAllChats(loadOlder = false): void {
    const restaurantId = this.currentRestaurant?.restaurant_id;
    if (!restaurantId) {
      this.toastr.error('Restaurant not loaded. Please try again.');
      return;
    }

    const cursor = loadOlder ? this.chatsCursor : '';

    if (loadOlder) {
      this.chatsLoadingMore = true;
    } else {
      this.chatsLoading = true;
      this.allChats = [];
      this.chatsCursor = '';
    }

    this.dashboardService.getAllChats(restaurantId, cursor, this.chatsListLimit).subscribe({
      next: (response: any) => {
        const { data, apiError } = this.normalizeChatsListApiBody(response);

        if (apiError || !data) {
          if (!loadOlder) {
            this.allChats = [];
            this.chatsCursor = '';
          }
          return;
        }

        const incoming = this.mapApiChatSummaries(data.chats ?? []);

        if (loadOlder) {
          this.allChats = this.sortChatsByUpdatedDesc(
            this.mergeChatSummaries(this.allChats, incoming),
          );
        } else {
          this.allChats = this.sortChatsByUpdatedDesc(incoming);
        }

        this.chatsCursor = data.nextCursor ?? '';
      },
      error: () => {
        if (!loadOlder) {
          this.allChats = [];
        }
        this.toastr.error('Unable to load chat history. Please try again.');
      },
      complete: () => {
        this.chatsLoading = false;
        this.chatsLoadingMore = false;
      },
    });
  }

  private normalizeChatsListApiBody(response: any): {
    data: { chats?: unknown[]; nextCursor?: string } | null;
    apiError: { code?: number; message?: string } | null;
  } {
    if (response?.status >= 400 && response?.error && typeof response.error === 'object') {
      const body = response.error;
      return {
        data: body.data ?? null,
        apiError: body.error ?? null,
      };
    }

    return {
      data: response?.data ?? null,
      apiError: response?.error ?? null,
    };
  }

  private mapApiChatSummaries(raw: unknown[]): RestaurantChatSummary[] {
    return raw
      .filter((item) => item && typeof item === 'object')
      .map((item) => {
        const row = item as Record<string, unknown>;
        return {
          restaurantId: String(row['restaurantId'] ?? ''),
          chatId: String(row['chatId'] ?? ''),
          chatType: String(row['chatType'] ?? 'general'),
          insightId: row['insightId'] == null ? null : String(row['insightId']),
          createdAt: String(row['createdAt'] ?? ''),
          updatedAt: String(row['updatedAt'] ?? ''),
          title:
            row['title'] != null
              ? String(row['title'])
              : row['name'] != null
                ? String(row['name'])
                : undefined,
        };
      })
      .filter((chat) => !!chat.chatId);
  }

  private mergeChatSummaries(
    existing: RestaurantChatSummary[],
    incoming: RestaurantChatSummary[],
  ): RestaurantChatSummary[] {
    const seen = new Set(existing.map((c) => c.chatId));
    const merged = [...existing];
    for (const chat of incoming) {
      if (!seen.has(chat.chatId)) {
        seen.add(chat.chatId);
        merged.push(chat);
      }
    }
    return merged;
  }

  /** Newest activity first (1 min, 21 min, 1 hr, …). */
  private sortChatsByUpdatedDesc(chats: RestaurantChatSummary[]): RestaurantChatSummary[] {
    return [...chats].sort((a, b) => this.chatUpdatedAtMs(b) - this.chatUpdatedAtMs(a));
  }

  private chatUpdatedAtMs(chat: RestaurantChatSummary): number {
    const iso = chat.updatedAt || chat.createdAt;
    const ms = new Date(iso).getTime();
    return Number.isNaN(ms) ? 0 : ms;
  }

  formatChatListDate(iso: string): string {
    return formatUtcDateTime(iso);
  }

  /** User-facing title — custom name, else type-based default. */
  getChatListTitle(chat: RestaurantChatSummary): string {
    const custom = chat.title?.trim();
    if (custom) {
      return custom;
    }
    const type = (chat.chatType ?? 'general').toLowerCase();
    if (type === 'general') {
      return 'General conversation';
    }
    if (type === 'insight') {
      return 'Insight conversation';
    }
    return `${type.charAt(0).toUpperCase()}${type.slice(1)} conversation`;
  }

  closeChatListMenu(): void {
    this.openChatListMenuId = null;
  }

  toggleChatListMenu(chat: RestaurantChatSummary, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    if (this.isChatTitleEditActive()) {
      return;
    }
    this.openChatListMenuId = this.openChatListMenuId === chat.chatId ? null : chat.chatId;
  }

  isChatListMenuOpen(chatId: string): boolean {
    return this.openChatListMenuId === chatId;
  }

  onChatMenuViewMessages(chat: RestaurantChatSummary, event: Event): void {
    if (this.isChatTitleEditActive()) {
      return;
    }
    this.closeChatListMenu();
    this.viewChatMessage(chat, event);
  }

  isEditingChatTitle(chatId: string): boolean {
    return this.editingChatTitleId === chatId;
  }

  /** True while any row is in inline title edit mode. */
  isChatTitleEditActive(): boolean {
    return this.editingChatTitleId !== null;
  }

  onChatListRowClick(chat: RestaurantChatSummary, event: Event): void {
    if (this.isChatTitleEditActive()) {
      event?.stopPropagation();
      event?.preventDefault();
      return;
    }
    this.viewChatMessage(chat, event);
  }

  onChatMenuRename(chat: RestaurantChatSummary, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.closeChatListMenu();
    this.startEditingChatTitle(chat);
  }

  startEditingChatTitle(chat: RestaurantChatSummary): void {
    if (this.renamingChatId || !chat.chatId) {
      return;
    }

    this.closeChatListMenu();
    this.editingChatTitleId = chat.chatId;
    this.editingChatTitleOriginal = this.getChatListTitle(chat);
    this.editingChatTitleDraft = this.editingChatTitleOriginal;

    setTimeout(() => {
      const input = this.chatTitleInput?.nativeElement;
      if (!input) {
        return;
      }
      input.focus();
      input.select();
    }, 0);
  }

  cancelEditingChatTitle(): void {
    this.chatTitleBlurCommitDisabled = true;
    this.editingChatTitleId = null;
    this.editingChatTitleDraft = '';
    this.editingChatTitleOriginal = '';
    setTimeout(() => {
      this.chatTitleBlurCommitDisabled = false;
    }, 0);
  }

  onChatTitleInputKeydown(chat: RestaurantChatSummary, event: KeyboardEvent): void {
    event.stopPropagation();
    if (event.key === 'Enter') {
      event.preventDefault();
      this.commitChatTitleRename(chat);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEditingChatTitle();
    }
  }

  onChatTitleInputBlur(chat: RestaurantChatSummary): void {
    if (this.chatTitleBlurCommitDisabled) {
      return;
    }
    if (this.editingChatTitleId !== chat.chatId) {
      return;
    }
    this.commitChatTitleRename(chat);
  }

  commitChatTitleRename(chat: RestaurantChatSummary): void {
    if (this.editingChatTitleId !== chat.chatId) {
      return;
    }

    const nextTitle = this.editingChatTitleDraft.trim();
    const original = this.editingChatTitleOriginal;
    this.editingChatTitleId = null;
    this.editingChatTitleDraft = '';
    this.editingChatTitleOriginal = '';

    if (!nextTitle || nextTitle === original) {
      return;
    }

    const restaurantId = this.currentRestaurant?.restaurant_id;
    if (!restaurantId || !chat.chatId) {
      return;
    }

    this.renamingChatId = chat.chatId;
    this.dashboardService
      .updateCommonChat(restaurantId, chat.chatId, { title: nextTitle })
      .subscribe({
        next: (response: any) => {
          if (response?.error || response?.status >= 400) {
            this.toastr.error(
              response?.error?.message ?? 'Unable to rename chat. Please try again.',
            );
            return;
          }

          this.allChats = this.allChats.map((c) =>
            c.chatId === chat.chatId ? { ...c, title: nextTitle } : c,
          );

          if (this.chatPanelChatId === chat.chatId) {
            this.chatPanelSubtitle = nextTitle;
          }

          this.toastr.success('Chat renamed.');
        },
        error: () => {
          this.toastr.error('Unable to rename chat. Please try again.');
        },
        complete: () => {
          this.renamingChatId = null;
        },
      });
  }

  onChatMenuDeleteChat(chat: RestaurantChatSummary, event: Event): void {
    this.closeChatListMenu();
    this.deleteChatFromHistory(chat, event);
  }

  /** Updated time in chat history list (`01 Jun, 2026 11:47 AM` UTC). */
  formatChatListRelativeDate(iso: string): string {
    return formatUtcChatListUpdatedAt(iso);
  }

  getChatListDateTooltip(chat: RestaurantChatSummary): string {
    const updated = this.formatChatListDate(chat.updatedAt);
    const created = this.formatChatListDate(chat.createdAt);
    if (created && created !== updated) {
      return `Updated ${updated} · Started ${created}`;
    }
    return updated ? `Updated ${updated}` : '';
  }

  trackChatListItem(_index: number, chat: RestaurantChatSummary): string {
    return chat.chatId;
  }

  isDeletingChat(chatId: string): boolean {
    return this.deletingChatId === chatId;
  }

  deleteChatFromHistory(chat: RestaurantChatSummary, event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    const restaurantId = this.currentRestaurant?.restaurant_id;
    if (!restaurantId || !chat.chatId || this.deletingChatId) {
      return;
    }

    this.deletingChatId = chat.chatId;
    this.dashboardService.deleteChat(restaurantId, chat.chatId).subscribe({
      next: (response: any) => {
        if (response?.error || response?.status >= 400) {
          this.toastr.error(response?.error?.message ?? 'Unable to delete chat. Please try again.');
          return;
        }

        this.allChats = this.allChats.filter((c) => c.chatId !== chat.chatId);

        if (this.chatPanelChatId === chat.chatId) {
          this.chatPanelChatId = '';
          if (this.chatPanelOpen) {
            this.closeChatPanel();
          }
        }

        this.toastr.success('Chat deleted.');
      },
      error: () => {
        this.toastr.error('Unable to delete chat. Please try again.');
      },
      complete: () => {
        this.deletingChatId = null;
      },
    });
  }

  viewChatMessage(chat: RestaurantChatSummary, event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();

    const restaurantId = this.currentRestaurant?.restaurant_id;
    if (!restaurantId || !chat.chatId) {
      this.toastr.error('Unable to open chat. Please try again.');
      return;
    }

    this.closeChatListPanel();
    this.stopChatStream();
    this.stopCommonChatStream();
    this.chatOpenedFromHistory = true;

    this.chatPanelInsightId = null;
    this.chatPanelInsightValue = null;
    this.chatPanelName = null;
    this.chatPanelSubtitle = this.getChatListTitle(chat);
    this.chatPanelChatId = chat.chatId;
    this.setPanelMessages([]);
    this.panelComposer = '';
    this.chatCursor = '';

    this.openChatPanel();
    this.getExistingChatMessages(false);
    this.startCommonChatStream();
  }

  get isCancellationScheduled(): boolean {
    return !!this.subscriptionDetails?.cancelAtPeriodEnd;
  }

  get isPastDue(): boolean {
    return this.subscriptionDetails?.status === 'PAST_DUE';
  }

  get isFreePlan(): boolean {
    return this.currentPlanName === 'Free';
  }

  /** Whether upgrading right now would still be free — only true while an active
   * promo (Stripe trial_end) covers the restaurant, never assumed otherwise. */
  get hasActivePromotion(): boolean {
    return !!this.subscriptionDetails?.activePromotionId;
  }

  convertSecondsToUTCTime(seconds: any): string {
    // formatUtcDateTime() takes an ISO string (`new Date(iso)` inside it) — a raw
    // epoch-seconds number passed directly is misread by `Date` as milliseconds,
    // landing in 1970. Convert to ms and round-trip through an ISO string first.
    if (seconds === null || seconds === undefined || isNaN(seconds)) {
      return '';
    }
    return formatUtcDateTime(new Date(Number(seconds) * 1000).toISOString());
  }

  /** Same epoch-seconds input as convertSecondsToUTCTime(), without the time-of-day. */
  convertSecondsToUTCDate(seconds: any): string {
    if (seconds === null || seconds === undefined || isNaN(seconds)) {
      return '';
    }
    return formatUtcDate(new Date(Number(seconds) * 1000).toISOString());
  }
}
